import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { QUERY_KEYS } from "@/lib/queryKeys";
import { getDefaultDenyMessage } from "@/constants/denyMessages";
import { retryDelayByErrorKind, shouldRetryByErrorKind, throwApiErrorFromResponse } from '@/lib/errorFlow';
import { incrementCompatMetric } from '@/lib/compatMetrics';

export type CrudAction = "view" | "create" | "update" | "delete";

export type MenuPermission = {
  menu_id: string;
  menu_path: string;
  can_view: boolean;
  can_create: boolean;
  can_update: boolean;
  can_delete: boolean;
};

type MyPermissionsPayload = {
  permissions: MenuPermission[];
  is_admin?: boolean;
};

type MyPermissionsResponse = {
  success: boolean;
  message?: string;
  data?: MyPermissionsPayload;
};

const toBool = (v: unknown): boolean => v === true || v === 1 || v === "1" || v === "true";

const DISABLE_OVERVIEW_PERMISSION_FALLBACK = (() => {
  const raw = String(import.meta.env.VITE_DISABLE_OVERVIEW_PERMISSION_FALLBACK ?? '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
})();

export const resolveMenuPath = (pathname: string): string => {
  // Dashboard canonicalization: /overview is canonical, / is legacy alias.
  if (pathname === "/" || pathname.startsWith("/overview")) return "/overview";

  // Map detail routes back to their parent menu permission
  if (pathname.startsWith("/permissions/create-account")) return "/permissions/accounts";
  if (pathname.startsWith("/permissions/edit-account")) return "/permissions/accounts";
  // IW3.16.B2: /permissions/delegations is same page as /permissions/roles (tabs); guard by roles view until backend has menu_item /permissions/delegations
  if (pathname === "/permissions/delegations" || pathname.startsWith("/permissions/delegations/")) return "/permissions/roles";

  // Config routes (new structure)
  if (pathname.startsWith("/config/overview-templates")) return "/config/overview-templates";
  if (pathname.startsWith("/config/overview")) return "/config/overview";
  // Incidents Config - consolidated routing
  if (pathname.startsWith("/incidents-config")) return "/incidents-config";
  // Legacy: old /config/incidents paths (backward compatibility)
  if (pathname.startsWith("/config/incidents/kpi-policies")) return "/incidents-config";
  if (pathname.startsWith("/config/incidents/step-templates")) return "/incidents-config";
  if (pathname === "/config/incidents" || pathname.startsWith("/config/incidents/")) return "/incidents-config";
  
  // Backward compatibility: old /settings path (should redirect via App.tsx, but handle here for safety)
  if (pathname.startsWith("/settings")) return "/config/overview";

  // Projects: map to specific submenu permissions
  if (pathname.startsWith("/projects/shares")) return "/projects/assignment";
  if (pathname.startsWith("/projects/duty-schedules")) return "/projects/duty-schedules";
  if (pathname.startsWith("/projects/list")) return "/projects/list";
  if (pathname.startsWith("/projects/assignment")) return "/projects/assignment";
  // Default /projects route goes to list
  if (pathname === "/projects") return "/projects/list";

  // Backward compatibility: old /incidents/templates path
  if (pathname.startsWith("/incidents/templates")) return "/incidents-config";

  // System Sizing: Admin Config is nested under menu path /optimization/sizing
  // (User execution /optimization/sizing is S7; Survey /optimization/survey is separate).
  // Must NOT collapse sizing/config to /optimization (would widen access incorrectly).
  if (pathname === "/optimization/sizing" || pathname.startsWith("/optimization/sizing/")) {
    return "/optimization/sizing";
  }
  if (pathname === "/optimization/survey" || pathname.startsWith("/optimization/survey/")) {
    return "/optimization/survey";
  }

  // S7.3: exact legacy `/optimization` is a migration alias → User Sizing permission gate.
  // AdminLayout checks can_view BEFORE <Navigate> runs; without this alias, users who only
  // have /optimization/sizing (child inherited perms) would be denied before redirect.
  // Exact path only — never wildcard /optimization/*.
  if (pathname === "/optimization" || pathname === "/optimization/") {
    return "/optimization/sizing";
  }

  return pathname;
};

export const useMyPermissions = () => {
  const location = useLocation();
  
  // Only fetch when token exists (prevent 401 spam on auth routes)
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  
  const query = useQuery({
    // Permissions don't change per route, so use stable query key
    // This prevents unnecessary refetches and race conditions on navigation
    queryKey: QUERY_KEYS.permissions.myPermissions(''),
    queryFn: async () => {
      const res = await api.get<MyPermissionsResponse>("/roles/permissions/me");
      if (res.ok !== true) {
        throwApiErrorFromResponse(res, 'Không thể tải quyền', 'useMyPermissions:/roles/permissions/me');
      }

      const payload = (res.data as any)?.data as MyPermissionsPayload | undefined;
      const rawPerms = Array.isArray(payload?.permissions) ? payload!.permissions : [];

      const permissions: MenuPermission[] = rawPerms.map((p: any) => ({
        menu_id: String(p.menu_id ?? ""),
        menu_path: String(p.menu_path ?? ""),
        can_view: toBool(p.can_view),
        can_create: toBool(p.can_create),
        can_update: toBool(p.can_update),
        can_delete: toBool(p.can_delete),
      }));

      return {
        permissions,
        isAdmin: !!payload?.is_admin,
      };
    },
    enabled: !!token, // Only fetch when token exists
    staleTime: 60_000, // 60s – avoid refetch on route/focus that causes loading flip
    refetchOnWindowFocus: false, // Bootstrap once; no refetch on tab focus
    refetchOnMount: false, // Use cache when remounting (e.g. route change)
    retry: shouldRetryByErrorKind,
    retryDelay: retryDelayByErrorKind,
  });

  const permissionsByPath = useMemo(() => {
    const map = new Map<string, MenuPermission>();
    for (const p of query.data?.permissions ?? []) {
      if (p.menu_path) map.set(p.menu_path, p);
    }
    return map;
  }, [query.data?.permissions]);

  const can = (menuPath: string, action: CrudAction): boolean => {
    // Admin bypass
    if (query.data?.isAdmin) return true;

    const canonicalPath = resolveMenuPath(menuPath);
    // Temporary compatibility: if canonical key is missing, try legacy dashboard root.
    const canonicalPerm = permissionsByPath.get(canonicalPath);
    const legacyPerm =
      canonicalPath === "/overview" && !DISABLE_OVERVIEW_PERMISSION_FALLBACK ? permissionsByPath.get("/") : undefined;
    const perm = canonicalPerm ?? legacyPerm;
    if (canonicalPath === "/overview" && !canonicalPerm && legacyPerm) {
      incrementCompatMetric('compat.permission_fallback_used', { dedupeKey: 'permFallback:/overview', dedupeMs: 10_000 });
    }
    if (!perm) return false;
    if (action === "view") return perm.can_view;
    if (action === "create") return perm.can_create;
    if (action === "update") return perm.can_update;
    return perm.can_delete;
  };

  // Helper to check view permission (most common case)
  const canView = (menuPath: string): boolean => {
    return can(menuPath, "view");
  };

  return {
    permissions: query.data?.permissions ?? [],
    permissionsByPath,
    isAdmin: query.data?.isAdmin ?? false,
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    error: query.error,
    refetch: query.refetch,
    can,
    canView, // Export canView helper
  };
};

export const useMenuPermission = (menuPath: string) => {
  const { permissionsByPath, isAdmin, isLoading, can } = useMyPermissions();
  return {
    permission: permissionsByPath.get(menuPath) ?? null,
    isAdmin,
    isLoading,
    can,
  };
};

/**
 * Action-layer permission for simple CRUD (Phase IW3.12.B).
 * Returns { allowed, denyMessage } from menu permission + default deny message.
 * Use for PermissionButton when allowed = can(menuPath, action) only (no creator/assignee/Admin-Leader rule).
 * For context-based actions (e.g. canEditIncident), keep computing allowed at screen and use constants for denyMessage.
 */
export const useActionPermission = (menuPath: string, action: CrudAction) => {
  const { can } = useMenuPermission(menuPath);
  const allowed = can(menuPath, action);
  const denyMessage = getDefaultDenyMessage(menuPath, action);
  return { allowed, denyMessage };
};
